import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Info } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import LoadingSpinner from '../common/LoadingSpinner';
import { asPercent, useCrossInstrument } from '../../hooks/useMetricsApi';

const DIRECTION_TEXT = {
  improved: 'Improved',
  held: 'Held steady',
  declined: 'Declined',
};

// Kappa bands, with the honest label at the bottom. A point estimate of 0.005
// is not "slight agreement" -- it is indistinguishable from guessing, and
// calling it slight invites a reader to treat noise as a weak finding.
const kappaBand = (kappa) => {
  if (kappa === null || kappa === undefined) return 'not calculable';
  const magnitude = Math.abs(kappa);
  if (magnitude < 0.05) return 'indistinguishable from chance';
  if (kappa < 0) return 'worse than chance';
  if (kappa < 0.2) return 'slight';
  if (kappa < 0.4) return 'fair';
  if (kappa < 0.6) return 'moderate';
  if (kappa < 0.8) return 'substantial';
  return 'almost perfect';
};

const Crosstab = ({ table, directions }) => {
  const lookup = {};
  table.forEach((cell) => {
    lookup[`${cell.clinician}|${cell.student}`] = cell.count;
  });

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-separate border-spacing-0 text-sm">
        <thead>
          <tr>
            <th className="p-2 text-left text-xs font-medium text-slate-500">
              {'Clinician ↓ / Student →'}
            </th>
            {directions.map((direction) => (
              <th
                key={direction}
                className="p-2 text-center text-xs font-medium text-slate-500"
              >
                {DIRECTION_TEXT[direction]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {directions.map((row) => (
            <tr key={row}>
              <th className="p-2 text-left text-xs font-medium text-slate-600">
                {DIRECTION_TEXT[row]}
              </th>
              {directions.map((column) => {
                const count = lookup[`${row}|${column}`] || 0;
                const agrees = row === column;
                return (
                  <td
                    key={column}
                    className={`border border-slate-100 p-2 text-center ${
                      agrees ? 'bg-emerald-50 font-medium text-emerald-800' : 'text-slate-700'
                    }`}
                  >
                    {count || <span className="text-slate-300">0</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-slate-400">
        Shaded cells are semesters where both instruments reached the same verdict.
      </p>
    </div>
  );
};

const ItemsScored = ({ itemsScored }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
    {Object.entries(itemsScored).map(([side, spread]) => (
      <div key={side}>
        <p className="text-xs font-medium text-slate-600">
          Items behind the {side === 'clinician' ? 'clinician' : 'student'} verdict
        </p>
        <ul className="mt-1 space-y-0.5">
          {Object.entries(spread).map(([items, count]) => (
            <li key={items} className="text-xs text-slate-500">
              {count} semester{count === 1 ? '' : 's'} scored on {items} item
              {items === '1' ? '' : 's'}
            </li>
          ))}
        </ul>
      </div>
    ))}
  </div>
);

const CrossInstrument = () => {
  const [comparison, setComparison] = useState('from_intake');
  const [minItems, setMinItems] = useState(8);
  const { data, isLoading, error } = useCrossInstrument({ comparison, minItems });

  const field =
    'rounded-md border border-slate-300 bg-white px-2 py-1 text-sm text-slate-700';

  return (
    <div className="space-y-6 p-4">
      <div>
        <Link
          to="/dashboard/metrics"
          className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          <ArrowLeft className="h-4 w-4" />
          All instruments
        </Link>
        <h1 className="mt-2 text-xl font-semibold text-slate-900">
          Clinician assessment vs student survey
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Each semester gets one verdict from each instrument -- improved, held
          steady or declined, across all its items -- and the two are compared.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-6 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-500">Compared against</span>
          <select
            className={field}
            value={comparison}
            onChange={(event) => setComparison(event.target.value)}
          >
            <option value="from_intake">Intake (cumulative)</option>
            <option value="from_previous">Previous semester</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-500">
            Minimum items per verdict
          </span>
          <select
            className={field}
            value={minItems}
            onChange={(event) => setMinItems(Number(event.target.value))}
          >
            {[1, 4, 6, 8, 10].map((n) => (
              <option key={n} value={n}>
                {n} item{n === 1 ? '' : 's'}
              </option>
            ))}
          </select>
          <span className="max-w-xs text-xs text-slate-400">
            A verdict from three items and one from thirteen are not the same
            claim. Raising this drops thinly scored semesters.
          </span>
        </label>
      </div>

      {isLoading && (
        <div className="p-8">
          <LoadingSpinner />
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Could not load the comparison</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {data && data.matched === 0 && (
        <Alert>
          <AlertTitle>Nothing comparable at this threshold</AlertTitle>
          <AlertDescription>
            {data.msg ||
              `${data.excluded} matched semesters were scored on fewer than ${minItems} items.`}
          </AlertDescription>
        </Alert>
      )}

      {data && data.matched > 0 && (
        <>
          {data.inconclusive && (
            <Alert>
              <AlertTitle>This sample cannot settle the question</AlertTitle>
              <AlertDescription>
                The confidence interval spans both agreement and disagreement.
                The figures below describe these {data.matched} semesters; they
                are not evidence that the instruments do, or do not, agree in
                general.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs text-slate-500">Semesters compared</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">{data.matched}</p>
              {data.excluded > 0 && (
                <p className="mt-1 text-xs text-slate-400">
                  {data.excluded} excluded as too thinly scored
                </p>
              )}
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs text-slate-500">Raw agreement</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {asPercent(data.agreementRate)}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                Inflated when most semesters improve on both sides
              </p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <p className="text-xs text-slate-500">Agreement beyond chance (kappa)</p>
              <p className="mt-1 text-2xl font-semibold text-slate-900">
                {data.kappa === null ? 'n/a' : data.kappa.toFixed(3)}
              </p>
              <p className="mt-1 text-xs text-slate-400">
                {kappaBand(data.kappa)}
                {data.kappaCi &&
                  ` · 95% CI ${data.kappaCi[0].toFixed(2)} to ${data.kappaCi[1].toFixed(2)}`}
              </p>
            </div>
          </div>

          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">
              Where the verdicts landed
            </h2>
            <div className="mt-3">
              <Crosstab table={data.table} directions={data.directions} />
            </div>
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-sm font-semibold text-slate-900">When they disagreed</h2>
            <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-slate-500">Clinician saw more improvement</p>
                <p className="text-lg font-semibold text-slate-900">
                  {data.asymmetry.clinicianHigher} semesters
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Student reported more improvement</p>
                <p className="text-lg font-semibold text-slate-900">
                  {data.asymmetry.studentHigher} semesters
                </p>
              </div>
            </div>
            <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              A lopsided split would suggest one side is systematically more
              generous. An even one does not rule it out at this sample size.
            </p>
          </section>

          {data.headroomCorrelation && (
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-slate-900">
                Do students with more room to move improve more?
              </h2>
              <p className="mt-2 text-sm text-slate-700">
                Spearman correlation between the two instruments' starting
                headroom: {data.headroomCorrelation.rho.toFixed(2)} over{' '}
                {data.headroomCorrelation.n} semesters.
              </p>
              <p className="mt-1 text-xs text-slate-500">
                A high correlation means both instruments saw the same students as
                having the furthest to go.
              </p>
            </section>
          )}

          <section className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <h2 className="text-sm font-semibold text-slate-900">How much each verdict rests on</h2>
            <div className="mt-3">
              <ItemsScored itemsScored={data.itemsScored} />
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default CrossInstrument;
